import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  SVG_STICKERS,
  QUOTE_STICKERS,
  EMOJI_STICKERS,
  STICKY_STICKERS,
  GIF_STICKERS,
  type StickerItem,
  type StickerCategory,
} from "@/lib/sticker-library";

interface StickerPanelProps {
  onAdd: (item: StickerItem) => void;
  onClose: () => void;
}

type PanelTab = StickerCategory;

const TABS: { id: PanelTab; label: string; emoji: string }[] = [
  { id: "stickers",   label: "Stickers",   emoji: "✦"  },
  { id: "emojis",     label: "Emojis",     emoji: "😊" },
  { id: "stickies",   label: "Stickies",   emoji: "📝" },
  { id: "gifs",       label: "GIFs",       emoji: "🎞" },
];

function StickerThumb({ item, onAdd, wide }: { item: StickerItem; onAdd: () => void; wide?: boolean }) {
  const [gifLoaded, setGifLoaded] = useState(false);

  if (item.type === "emoji") {
    return (
      <button
        onClick={onAdd}
        className="aspect-square flex items-center justify-center rounded-xl bg-stone-50 hover:bg-amber-50 border border-stone-100 hover:border-amber-200 transition-all hover:scale-110 active:scale-95 text-3xl"
        title={item.label}
      >
        {item.content}
      </button>
    );
  }

  if (item.type === "gif") {
    return (
      <button
        onClick={onAdd}
        className="aspect-square rounded-xl overflow-hidden border border-stone-100 hover:border-amber-200 hover:scale-110 active:scale-95 transition-all relative"
        title={item.label}
      >
        {!gifLoaded && (
          <div className="absolute inset-0 bg-stone-100 animate-pulse flex items-center justify-center">
            <span className="text-xs text-stone-400">gif</span>
          </div>
        )}
        <img
          src={item.content}
          alt={item.label}
          className={cn("w-full h-full object-cover transition-opacity", gifLoaded ? "opacity-100" : "opacity-0")}
          onLoad={() => setGifLoaded(true)}
        />
      </button>
    );
  }

  // SVG sticker (including quotes — wide layout handled by grid)
  return (
    <button
      onClick={onAdd}
      className={cn(
        "flex items-center justify-center rounded-xl bg-stone-50 hover:bg-violet-50 border border-stone-100 hover:border-violet-200 transition-all hover:scale-105 active:scale-95 p-2",
        wide ? "aspect-square" : "aspect-square"
      )}
      title={item.label}
      dangerouslySetInnerHTML={{ __html: item.content }}
    />
  );
}

export function StickerPanel({ onAdd, onClose }: StickerPanelProps) {
  const [activeTab, setActiveTab] = useState<PanelTab>("stickers");

  const getItems = (): StickerItem[] => {
    if (activeTab === "stickers")  return [...SVG_STICKERS, ...QUOTE_STICKERS];
    if (activeTab === "emojis")    return EMOJI_STICKERS;
    if (activeTab === "stickies")  return STICKY_STICKERS;
    if (activeTab === "gifs")      return GIF_STICKERS;
    return [];
  };

  const isQuotes = false;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center"
      onClick={onClose}
    >
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" />

      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 32, stiffness: 380 }}
        className="relative w-full max-w-2xl rounded-t-3xl overflow-hidden"
        style={{
          background: "#f7f7f5",
          backgroundImage: `radial-gradient(circle, rgba(168,165,160,0.05) 1.2px, transparent 1.2px)`,
          backgroundSize: "18px 18px",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="w-10 h-1 bg-stone-300 rounded-full mx-auto mt-3 mb-1" />

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">🎨</span>
            <div>
              <h3 className="font-display font-semibold text-stone-800 text-base leading-tight">Make it yours</h3>
              <p className="text-[10px] text-stone-400 font-sans uppercase tracking-widest">tap to add to your journal</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-200 hover:bg-stone-300 transition-colors">
            <X className="w-4 h-4 text-stone-600" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1.5 px-5 pb-3 overflow-x-auto" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-sans font-medium transition-all",
                activeTab === tab.id
                  ? "bg-stone-800 text-white shadow-sm"
                  : "bg-white border border-stone-200 text-stone-600 hover:border-stone-400"
              )}
            >
              <span className="text-base leading-none">{tab.emoji}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Quotes tab subtitle */}
        <AnimatePresence>
          {isQuotes && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="px-5 pb-2 text-[10px] text-stone-500 font-sans uppercase tracking-widest"
            >
              Motivational · multilingual · self-love
            </motion.p>
          )}
        </AnimatePresence>

        {/* Content */}
        <div className="px-4 pb-8" style={{ maxHeight: "52vh", overflowY: "auto" }}>
          <div className={cn(
            "grid gap-2 py-1",
            isQuotes ? "grid-cols-3" : "grid-cols-5"
          )}>
            {getItems().map((item) => (
              <StickerThumb
                key={item.id}
                item={item}
                wide={isQuotes}
                onAdd={() => onAdd(item)}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
